import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FulfillmentDashboard, DashboardConfig, DashboardFilters, HttpClient, StageDrillDownContext } from '@fulfillment/web-modules';
import { AppState } from '../config/redux/reducers';
import AppConstants from '../constants/AppConstants';
import networkHandler from '../network/networkHandler';
import { EApiMiddlewareMethods } from '../network/networkHandler.types';

// Dashboard filter state lives only in the library's in-memory React state,
// so it's lost whenever this page unmounts — including a stage drill-down's
// navigate-to-Express-and-back round trip via the browser back button. Mirror
// it into this page's own URL query params so DashboardConfig.initialFilters
// can restore it when the page remounts on the way back.
const FILTER_PARAM_KEYS = ['activity', 'zone', 'vehicle'] as const;

function readFiltersFromUrl(search: string): DashboardFilters | undefined {
  const params = new URLSearchParams(search);
  const activity = params.get('activity');
  const zone = params.get('zone');
  const vehicle = params.get('vehicle');
  if (!activity && !zone && !vehicle) return undefined;
  return { activity, zone, vehicle };
}

const httpClient: HttpClient = {
  post: async (url, body) => {
    const { data } = await networkHandler(
      { url, method: EApiMiddlewareMethods.POST, data: body , headers: { 'x-client-id': 'lastmile-web' } },
      { isApigeeService: true, isAuth: true },
    );
    return data.data;
  },
  get: async (url) => {
    const { data } = await networkHandler(
      { url, method: EApiMiddlewareMethods.GET, headers: { 'x-client-id': 'lastmile-web' } },
      { isApigeeService: true, isAuth: true },
    );
    return data;
  },
};

const OpsDashboard = () => {
  const history = useHistory();
  const { countryCode, hubCode } = useSelector((state: AppState) => state.common);
  const country = sessionStorage.getItem(AppConstants.COUNTRY_CODE) || countryCode || AppConstants.COUNTRY_OBJ.value;

  const config: DashboardConfig = useMemo(
    () => ({
      httpClient,
      // Mirrors the real path selection: Wings is a Last Mile Dispatcher
      // consumer, so it always calls the /lastmile/ path.
      endpoint: 'v1/fulfilment/ops-tower/dashboard',
      filterOptionsEndpoint: 'v1/fulfilment/ops-tower/filters',
      store: hubCode,
      country,
      refreshIntervalSec: 60,
      initialFilters: readFiltersFromUrl(history.location.search),
      onFiltersChange: (filters) => {
        const params = new URLSearchParams(history.location.search);
        FILTER_PARAM_KEYS.forEach((key) => {
          const value = filters[key];
          if (value) {
            params.set(key, value);
          } else {
            params.delete(key);
          }
        });
        // replace, not push — keeping the URL in sync with filter picks
        // shouldn't grow the back-stack, or the back button would step
        // through each filter change instead of leaving the page.
        const search = params.toString();
        history.replace({ search: search ? `?${search}` : '' });
      },
      onError: (error) => console.error('FulfillmentDashboard failed to load', error),
      // Drill down into Express with the dashboard's current filters carried over.
      onStageDrillDown: (context: StageDrillDownContext) => {
        const params = new URLSearchParams({
          stage: context.stageId,
          status: context.status,
          // Express/index.tsx reads this on mount to pre-select the "ALL"
          // filter chip (ExpressSharedConstants.ALL_FILTER_CHIP) instead of
          // its usual UNASSIGNED default, so the drill-down lands showing
          // every bucket rather than just unassigned orders.
          chip: 'ALL',
          ...(context.filters.activity ? { activity: context.filters.activity } : {}),
          ...(context.filters.zone ? { zone: context.filters.zone } : {}),
          ...(context.filters.vehicle ? { vehicle: context.filters.vehicle } : {}),
        });
         history.push(
          `/express-delivery/express?${params.toString()}`,
          { consignmentIds: context.consignmentIds },
        );
      },
    }),
    [hubCode, country, history],
  );

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <FulfillmentDashboard config={config} />
    </div>
  );
};

export default OpsDashboard;
